// Reloading a display when the code underneath it changes.
//
// This exists because of a shape the deploy has: a display holds one page open
// for months, so the JavaScript running on a television is whatever was
// current the day somebody switched it on. The refresh added to App.jsx brings
// it new *content*, and nothing at all brings it new *code* - the first deploy
// after that shipped proved it, because every display already running kept the
// old bundle, which is precisely the one that never re-fetches anything.
//
// So the page has to notice and reload itself. Two signals, either of which is
// enough on its own:
//
//   - the API answers with a version it did not answer with before, which
//     rides free on the requests the refresh already makes; and
//   - index.html points at a bundle other than the one that is running, which
//     covers a frontend-only deploy the API never hears about.
import { onServerVersion } from '../../shared/http.js';

// Spread over a minute. A fleet is deployed to at once and would otherwise
// reload at once - a thundering herd against CloudFront and, worse, every
// display in the estate blank at the same moment.
const RELOAD_JITTER_MS = 60000;

// Only the hashed build output counts. Vite writes /assets/index-<hash>.js and
// a matching stylesheet, and the hash is the whole signal: same hash, same
// bytes.
//
// It is also what makes this inert in development, which matters more than it
// sounds. The dev server serves no /assets/ paths at all - it serves
// /src/main.jsx?t=<timestamp>, and that timestamp changes on every request. A
// check that compared everything would find a difference every time and put
// the page into a reload loop.
const HASHED_ASSET = /^\/assets\//;

function bundleFingerprint(doc) {
  const refs = [
    ...doc.querySelectorAll('script[src]'),
    ...doc.querySelectorAll('link[rel="stylesheet"][href]'),
  ]
    .map((el) => el.getAttribute('src') || el.getAttribute('href'))
    .filter((ref) => ref && HASHED_ASSET.test(ref));

  // Sorted, because the order tags appear in is the bundler's business and not
  // a version change.
  return refs.sort().join('|');
}

// Everything below is per-page-load state. A reload resets all of it, which is
// what stops a display reloading twice for one deploy: after it comes back,
// what it booted with *is* the new version.
let bootedBundle = null;
let bootedVersion = null;
let reloading = false;

function reloadSoon(reason) {
  if (reloading) return;
  reloading = true;

  const delay = Math.random() * RELOAD_JITTER_MS;
  // Logged rather than shown. A living room should not be told about a deploy,
  // but somebody with a remote debugger attached to a screen that is behaving
  // oddly should be able to see why it went away.
  console.info(`litbyt: ${reason}; reloading in ${Math.round(delay / 1000)}s`);
  setTimeout(() => window.location.reload(), delay);
}

// watchServerVersion starts comparing the build that answers each request
// against the first one that did. Call it once, and only on a paired display:
// reloading the pairing screen would throw away the code somebody is in the
// middle of typing into the admin site.
export function watchServerVersion() {
  onServerVersion((version) => {
    if (bootedVersion === null) {
      bootedVersion = version;
      return;
    }
    if (version !== bootedVersion) {
      reloadSoon(`API moved from ${bootedVersion} to ${version}`);
    }
  });
}

// checkForNewBundle asks whether the deployed frontend is still the one that
// is running. Cheap enough to ride the refresh tick: one conditional GET of a
// small file, against CloudFront rather than the API.
export async function checkForNewBundle() {
  if (reloading) return;

  if (bootedBundle === null) {
    bootedBundle = bundleFingerprint(document);
  }
  // No hashed assets means a dev server, or a build laid out in some way this
  // does not understand. Either way there is nothing to compare, and guessing
  // would mean reloading for ever.
  if (!bootedBundle) return;

  try {
    // no-store, or the browser answers this from the copy it already has and
    // the display never learns anything. CloudFront still caches it at the
    // edge, so detection lags a deploy by that TTL and no more.
    const response = await fetch('/index.html', { cache: 'no-store' });
    if (!response.ok) return;

    const deployed = bundleFingerprint(
      new DOMParser().parseFromString(await response.text(), 'text/html')
    );
    // An empty answer is a truncated or rewritten response, not a new build -
    // an origin error page would otherwise read as "everything changed".
    if (!deployed || deployed === bootedBundle) return;

    reloadSoon('a new frontend bundle is deployed');
  } catch (error) {
    // Offline, or the CDN is unhappy. The display keeps running what it has.
    console.error('Failed to check for a new bundle', error);
  }
}
